import { useMemo, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Link,
  Popover,
  Portal,
  Image,
} from "@chakra-ui/react";
import SuccessIcon from "../../assets/icons/success-icon.svg";
import InfoIcon from "../../assets/icons/info-icon.svg";
import WarningIcon from "../../assets/icons/warning-icon.svg";
import { useReferrals } from "../../hooks/queries/useReferrals";
import { formatParticipantDisplayName } from "../../helpers/string";

interface Notification {
  id: string;
  type: "success" | "info" | "warning";
  heading: string;
  description: string;
  timestamp: string;
  unread: boolean;
}

interface NotificationsProps {
  trigger: React.ReactNode;
}

export default function Notifications({ trigger }: NotificationsProps) {
  // Real, app-wide notifications: the most recent referrals (new cases from
  // Vision), newest first. Read state is tracked locally for the session.
  const { data } = useReferrals({ pageSize: 10 });
  const [readIds, setReadIds] = useState<Set<string>>(new Set());

  const notifications = useMemo<Notification[]>(() => {
    return (data?.data ?? []).map((referral) => {
      const id = `referral-${referral.id}`;
      const name =
        formatParticipantDisplayName(referral.participant);
      const description = referral.caseNumber
        ? `${name} · Case ${referral.caseNumber}`
        : name;
      return {
        id,
        type: "info" as const,
        heading: "New referral",
        description,
        timestamp: referral.referredDate ?? "",
        unread: !readIds.has(id),
      };
    });
  }, [data, readIds]);

  const markAllAsRead = () => {
    setReadIds(new Set(notifications.map((n) => n.id)));
  };

  const setAsRead = (id: string) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  return (
    <Popover.Root positioning={{ placement: "bottom-end" }}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Portal>
        <Popover.Positioner>
        <Popover.Content w="400px" maxH="500px" p={0} borderRadius={"16px"}>
          <Box p={4} borderBottom={"1px solid #D1DADF"}>
            <HStack justify="space-between" align="center">
              <Text fontSize="lg" fontWeight="400">
                Notifications
              </Text>
              <Link
                fontSize="sm"
                color="#0051D2"
                onClick={markAllAsRead}
                cursor="pointer"
                _hover={{ textDecoration: "underline" }}
              >
                Mark all as read
              </Link>
            </HStack>
          </Box>

          <VStack align="stretch" gap={0} maxH="400px" overflowY="auto">
            {notifications.length === 0 ? (
              <Box p={6} textAlign="center">
                <Text fontSize="sm" color="#6B7584">
                  You're all caught up
                </Text>
              </Box>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  setAsRead={setAsRead}
                />
              ))
            )}
          </VStack>
        </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

const NotificationItem = ({
  notification,
  setAsRead,
}: {
  notification: Notification;
  setAsRead: (id: string) => void;
}) => {
  const getIconBg = (type: string) => {
    switch (type) {
      case "success":
        return "green.100";
      case "info":
        return "blue.100";
      case "warning":
        return "orange.100";
      default:
        return "gray.100";
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case "success":
        return "green.600";
      case "info":
        return "blue.600";
      case "warning":
        return "orange.600";
      default:
        return "gray.600";
    }
  };

  const onClick = () => {
    // Set as read
    setAsRead(notification.id);
  };

  return (
    <Box
      key={notification.id}
      p={3}
      bg={notification.unread ? "gray.50" : "white"}
      _hover={{ bg: "gray.100" }}
      cursor="pointer"
      borderBottom={"1px solid #D1DADF"}
      onClick={onClick}
    >
      <HStack align="start" gap={3}>
        <NotificationTypeIcon type={notification.type} />

        <VStack align="start" gap={0} flex="1">
          <Text fontSize="sm" fontWeight="700">
            {notification.heading}
          </Text>
          <Text fontSize="xs" color="#6B7584" fontWeight={400}>
            {notification.description}
          </Text>
          <Text fontSize="xs" color="#6B7584" fontWeight={400}>
            {notification.timestamp}
          </Text>
        </VStack>
        {notification.unread && (
          <Box
            w="6px"
            h="6px"
            bg="#2970FF"
            borderRadius="full"
            flexShrink={0}
            mt={6}
          />
        )}
      </HStack>
    </Box>
  );
};

const NotificationTypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case "success":
      return (
        <Image
          src={SuccessIcon}
          alt="Success Icon"
          w="32px"
          h="32px"
          border="4px solid #ECFDF3"
          borderRadius="full"
          mt={2}
        />
      );
    case "info":
      return (
        <Image
          src={InfoIcon}
          alt="Info Icon"
          w="32px"
          h="32px"
          border="4px solid #EEF4FF"
          borderRadius="full"
          mt={2}
        />
      );
    case "warning":
      return (
        <Image
          src={WarningIcon}
          alt="Warning Icon"
          w="32px"
          h="32px"
          border="4px solid #FFFAEB"
          borderRadius="full"
          mt={2}
        />
      );
    default:
      return null;
  }
};
